import * as React from 'react';
import { BwjInput } from '../../entities/bwj-input';
import { firstOrDefault } from '../../utils/collection';
import { IBwjSelectFieldProps, IBwjSelectFieldState, BwjSelectFieldBase } from './common';
import { Dropdown, IDropdownOption } from '@fluentui/react';

export interface IBwjSingleSelectFieldProps<T> extends IBwjSelectFieldProps<T | null> {}

interface IState<T> extends IBwjSelectFieldState<T | null> {
    currentSelection: IDropdownOption | null;
}

export class BwjSingleSelectField<T> extends BwjSelectFieldBase<T | null, IState<T | null>> {
    constructor(props: IBwjSelectFieldProps<T | null>) {
      super(props);

      this.handleChange = this.handleChange.bind(this);

      const displayOptions = this.buildDisplayOptions(props.options);
      // figure out what the current selection should be, if anything
      let option: T | null = !!this.props.input ? this.props.input.value : null;
      const currentSelection = this.getDisplayOptionWithOption(option, displayOptions);

      this.state = {
          displayOptions,
          currentSelection,
          input: this.props.input || new BwjInput<T | null>(null),
      };
    }

    protected handleComponentDidMount() {
        const displayOptions = this.buildDisplayOptions(this.props.options);
        let option: T | null = !!this.props.input ? this.props.input.value : null;
        const currentSelection = this.getDisplayOptionWithOption(option, displayOptions);

        this.setState({
            displayOptions,
            currentSelection,
            input: this.props.input || new BwjInput<T | null>(null)
        });
    }

    public handleComponentDidUpdate(prevProps:IBwjSingleSelectFieldProps<T | null>) {
        // check for a change to the options, which we set on state
        if(prevProps.options !== this.props.options) {
            let changes:Partial<IState<T | null>> = {
                displayOptions: this.buildDisplayOptions(this.props.options)
            };

            // if a selection was already made, that selection may not be one of the new options, in which case
            // a change in selection must be made and reported
            if(!!this.state.currentSelection) {
                const currentSelection = firstOrDefault(changes.displayOptions || [], o => o.key === this.state.currentSelection?.key);

                // we only want to trigger a value update if the current selection object reference changed
                if(!currentSelection) {
                    changes.currentSelection = currentSelection;
                    let value = new BwjInput<T | null>(null);
                    value.validity = prevProps.input.validity;
                    value.message = prevProps.input.message;
    
                    changes.input = value;
                    this.performFieldUpdate = true;
                }
            }

            this.setState(changes as IState<T | null>);
        }
        // update the input value if changed externally
        const incomingSelection = !!this.props.input.value ? this.getDisplayOptionWithOption(this.props.input.value, this.state.displayOptions) : null;
        if(incomingSelection !== this.state.currentSelection) {
            this.setState({
                currentSelection: incomingSelection,
                input: this.props.input
            });
        }
        // revalidate/update the field due to a selection change.  We don't do this on blur, as we normally
        // would with other fields
        else if(this.performFieldUpdate) {
            this.performFieldUpdate = false;
            this.updateInputField();
        }
    }

    public handleChange(e: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        const val = !!option ? option.data : null;
        const value = new BwjInput<T | null>(val);
        value.validity = this.state.input.validity;
        value.message = this.state.input.message;

        this.setState({
            currentSelection: option || null,
            input: value
        });
    }

    protected valuesAreEqual(prev: any, current: any) {
        if(prev === current) { return true; }
        if(!prev || !current) { return false; }

        const prevOpt = this.getDisplayOptionWithOption(prev, this.state.displayOptions);
        const currentOpt = this.getDisplayOptionWithOption(current, this.state.displayOptions);

        return prevOpt === currentOpt;
    }

    protected handleValueDidChange(prev: BwjInput<T | null>, curr: BwjInput<T | null>) {
        this.setState({
            currentSelection: this.getDisplayOptionWithOption(curr.value, this.state.displayOptions)
        });
    }

    protected handleFocus() {
        // this is done to defeat a quirk with the dropdown component, which fires 'has changed' event with the first option
        // when clicked, before focus is fired.  Since we fortunately set the value on state with the old raw value on focus,
        // we do the same here to revert the ill effects of this behavior
        this.setState({
            currentSelection: this.state.currentSelection
        });
    }
  
    public render() {
      const p = this.props;
      const s = this.state;
      return (
        <Dropdown  required={p.required}
                label={p.label}
                placeholder={p.placeholder}
                onChange={this.handleChange}
                onFocus={this.focusHandler}
                onBlur={this.handleDropdownBlur}
                onDismiss={this.handleCalloutDismiss}
                options={s.displayOptions}
                selectedKey={!!s.currentSelection ? s.currentSelection.key.toString() : null}
                errorMessage={s.input.message} />
      );
    }

    private performFieldUpdate:boolean = false;
  }